import React, { useEffect, useState } from "react";
import {
  Button,
  Container,
  FormGroup,
  Label,
} from "reactstrap";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import { FaRegEye, FaTimes, FaUserCheck } from "react-icons/fa";
import CompanySelector from "ATSComponents/Common/CompanySelector";
import OrgTableShimmer from "ATSComponents/Common/OrgTableShimmer";
import axios from "axios";
import CandidateLogsTimelineModal from "./CandidateLogsTimelineModal";
import ViewCandidatePopupModal, { normalizeStageLogs } from "./ViewCandidatePopupModal";

const sortCandidateRows = (rows, field, order) => {
  if (!field || !order) return rows;
  return [...rows].sort((left, right) => {
    const a = String(left?.[field] ?? "").toLowerCase();
    const b = String(right?.[field] ?? "").toLowerCase();
    return a.localeCompare(b, undefined, { numeric: true }) * order;
  });
};

const SelectedCandidates = () => {
  document.title = "ATS - Selected Candidates";

  const [candidateListData, setCandidateListData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 10,
    page: 0,
  });
  const [sortState, setSortState] = useState({ field: null, order: null });
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [viewCandidatePopupShow, setViewCandidatePopupShow] = useState(false);
  const [logsTimelineShow, setLogsTimelineShow] = useState(false);
  const [selectedLogsCandidate, setSelectedLogsCandidate] = useState(null);
  const [selectedCandidateDetails, setSelectedCandidateDetails] = useState(null);
  const [selectedCandidateIndex, setSelectedCandidateIndex] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterJobTitle, setFilterJobTitle] = useState("");
  const [jobTitleList, setJobTitleList] = useState([]);
  const [jobTitlesLoading, setJobTitlesLoading] = useState(false);
  const [jobTitleSearch, setJobTitleSearch] = useState("");
  const [jobTitlePage, setJobTitlePage] = useState(1);
  const [jobTitleTotalCount, setJobTitleTotalCount] = useState(0);
  const totalJobTitlePages = Math.max(1, Math.ceil(jobTitleTotalCount / 10));

  const userInfos = JSON.parse(sessionStorage.getItem("user-info"));
  const initialCompanyId =
    userInfos?.user_details?.companies_data?.[0]?.company_id;

  useEffect(() => {
    if (initialCompanyId) {
      setCompanyId(initialCompanyId);
    }
  }, [initialCompanyId]);

  useEffect(() => {
    if (!companyId) return;

    const fetchCandidateList = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          "/api/v1/candidates/candidates-by-status/",
          {
            params: {
              status: "Placed",
              company_id: companyId,
              page: lazyParams.page + 1,
              size: lazyParams.rows,
              ...(filterJobTitle ? { job_id: filterJobTitle } : {}),
            },
          }
        );

        setCandidateListData(response.data.results || []);
        setTotalRecords(response.data.count || 0);
      } catch (error) {
        console.error("Error fetching candidate list:", error);
        setCandidateListData([]);
        setTotalRecords(0);
      } finally {
        setLoading(false);
      }
    };

    fetchCandidateList();
  }, [companyId, lazyParams, filterJobTitle]);

  const getJobTitles = async (selectedCompanyId, page = 1, search = "") => {
    setJobTitlesLoading(true);
    try {
      const response = await axios.get("/api/v1/create-job-order/", {
        params: {
          ...(selectedCompanyId ? { company_id: selectedCompanyId } : {}),
          page,
          size: 10,
          ...(search ? { search } : {}),
        },
      });

      const data = response.data?.results || response.data?.data || [];
      const totalCount = response.data?.count || 0;
      const jobMap = new Map();

      data.forEach(item => {
        const id = item.job_id ?? item.id;
        const title = item.job_title ?? item.position ?? item.job_order_id;
        if (id && title) jobMap.set(String(id), { id, title });
      });

      setJobTitleList(Array.from(jobMap.values()));
      setJobTitleTotalCount(totalCount);
      setJobTitlePage(page);
    } catch (error) {
      console.error("Error fetching job titles:", error);
      setJobTitleList([]);
      setJobTitleTotalCount(0);
    } finally {
      setJobTitlesLoading(false);
    }
  };

  useEffect(() => {
    if (!companyId) return;
    setJobTitleList([]);
    setJobTitlePage(1);
    setJobTitleTotalCount(0);
    getJobTitles(companyId, 1, "");
  }, [companyId]);

  const handleCompanyChange = company => {
    if (company?.company_id) {
      setCompanyId(company.company_id);
      setFilterJobTitle("");
      setJobTitleSearch("");
      setLazyParams(prev => ({ ...prev, first: 0, page: 0 }));
    }
  };

  const handleViewCandidate = (candidate = null) => {
    if (candidate) {
      const rowIndex = candidateListData.findIndex(
        item => (item.candidate_id || item.id) === (candidate.candidate_id || candidate.id)
      );
      setSelectedCandidateDetails(candidate);
      setSelectedCandidateIndex(rowIndex >= 0 ? rowIndex : null);
      setViewCandidatePopupShow(true);
      return;
    }

    setViewCandidatePopupShow(false);
    setSelectedCandidateDetails(null);
    setSelectedCandidateIndex(null);
  };

  const handlePreviousCandidate = () => {
    if (selectedCandidateIndex === null || selectedCandidateIndex <= 0) return;
    const nextIndex = selectedCandidateIndex - 1;
    setSelectedCandidateDetails(candidateListData[nextIndex]);
    setSelectedCandidateIndex(nextIndex);
  };

  const handleNextCandidate = () => {
    if (selectedCandidateIndex === null || selectedCandidateIndex >= candidateListData.length - 1) return;
    const nextIndex = selectedCandidateIndex + 1;
    setSelectedCandidateDetails(candidateListData[nextIndex]);
    setSelectedCandidateIndex(nextIndex);
  };

  const handleViewLogs = rowData => {
    setSelectedLogsCandidate(rowData);
    setLogsTimelineShow(true);
  };

  const viewCandidateExperience = rowData => {
    const [yearPart, monthPart = "0"] = String(rowData?.total_experience || "0.0").split(".");
    const years = Number(yearPart) || 0;
    const months = Number(monthPart[0]) || 0;

    return (
      <span className="bg-light px-2 py-1 rounded d-inline-block">
        {years} {years === 1 ? "Year" : "Years"}
        {months > 0 && ` ${months} ${months === 1 ? "Month" : "Months"}`}
      </span>
    );
  };

  const viewCandidateDetails = rowData => (
    <Button
      className="btn btn-light"
      onClick={() => handleViewCandidate(rowData)}
      title="View Candidate Details"
    >
      <FaRegEye />
    </Button>
  );

  const stageLogsTemplate = rowData => {
    const logs = normalizeStageLogs(rowData);
    const latestLog = logs[logs.length - 1];

    if (!latestLog) {
      return <span className="text-muted small">No logs</span>;
    }

    return (
      <button
        type="button"
        className="text-start"
        onClick={() => handleViewLogs(rowData)}
        title="View logs timeline"
        style={{
          minWidth: 220,
          border: "1px solid #e2e8f0",
          borderRadius: 8,
          background: "#f8fafc",
          padding: "8px 10px",
          cursor: "pointer",
          width: "100%",
        }}
      >
        <div className="fw-semibold" style={{ color: "#1e293b", fontSize: 13 }}>
          {latestLog.stage}
        </div>
        <div className="text-muted text-truncate" style={{ maxWidth: 260, fontSize: 12 }}>
          {latestLog.jobTitle}
        </div>
        <div className="text-muted text-truncate" style={{ maxWidth: 260, fontSize: 12 }}>
          Reason : {latestLog.reason || "-"}
        </div>
         <div className="text-muted text-truncate" style={{ maxWidth: 260, fontSize: 12 }}>
          Updated By : {latestLog.createdBy || "-"}
        </div>
        <div className="text-muted text-truncate" style={{ maxWidth: 260, fontSize: 12 }}>
          Updated At : {latestLog.timestamp || "-"}
        </div>
      </button>
    );
  };

  const viewIsSelectedStatus = () => (
    <span className="fw-semibold text-success">Yes</span>
  );

  const getNameInitials = name =>
    name
      ? name
          .trim()
          .split(/\s+/)
          .map(part => part[0])
          .slice(0, 2)
          .join("")
          .toUpperCase()
      : "?";
  const getInitialsStyle = name => {
    const palette = [
      { background: "#eef2ff", color: "#4f46e5", borderColor: "#c7d2fe" },
      { background: "#fce7f3", color: "#be185d", borderColor: "#f9a8d4" },
      { background: "#dcfce7", color: "#15803d", borderColor: "#86efac" },
      { background: "#ffedd5", color: "#c2410c", borderColor: "#fdba74" },
      { background: "#cffafe", color: "#0e7490", borderColor: "#67e8f9" },
    ];
    const index = String(name || "")
      .split("")
      .reduce((sum, char) => sum + char.charCodeAt(0), 0) % palette.length;
    return palette[index];
  };

  const candidateNameTemplate = rowData => {
  const candidateName = [
    rowData.first_name,
    rowData.middle_name,
    rowData.last_name,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="candidate-name-with-initials">
      <span
        className="candidate-name-initials"
        style={getInitialsStyle(candidateName)}
      >
        {getNameInitials(candidateName)}
      </span>
      <span
        onClick={() => handleViewCandidate(rowData)}
        style={{
          color: "#0d6efd",
          cursor: "pointer",
          fontWeight: 600,
          textDecoration: "underline",
        }}
        title="View Resume"
      >
        {candidateName}
      </span>
    </div>
  );
};

  const jobOrderFilter = (
    <div style={{ minWidth: 240 }}>
      <Dropdown
        key={String(companyId || "no-company")}
        value={filterJobTitle}
        options={jobTitleList}
        optionLabel="title"
        optionValue="id"
        showClear
        placeholder="All Jobs"
        className="job-title-filter-dropdown w-100"
        loading={jobTitlesLoading}
        emptyMessage={jobTitlesLoading ? "Loading job orders..." : "No jobs found"}
        filter
        filterBy="title"
        filterInputAutoFocus={false}
        filterPlaceholder="Search job..."
        onChange={e => {
          setFilterJobTitle(e.value || "");
          setLazyParams(prev => ({ ...prev, first: 0, page: 0 }));
        }}
        filterTemplate={() => (
          <div className="d-flex align-items-center gap-1 px-2 py-2" style={{ borderBottom: "1px solid #e2e8f0" }} onMouseDown={e => e.stopPropagation()}>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search job & press Enter..."
              value={jobTitleSearch}
              onChange={e => {
                const value = e.target.value;
                setJobTitleSearch(value);
                if (!value.trim()) getJobTitles(companyId, 1, "");
              }}
              onKeyDown={e => {
                e.stopPropagation();
                if (e.key === "Enter") {
                  e.preventDefault();
                  getJobTitles(companyId, 1, jobTitleSearch);
                }
              }}
              style={{ flex: 1 }}
            />
            {jobTitlesLoading && <ProgressSpinner style={{ width: 18, height: 18 }} strokeWidth="6" animationDuration=".8s" />}
            {jobTitleSearch && (
              <button
                type="button"
                className="btn btn-sm btn-link p-0 text-secondary"
                title="Clear search"
                disabled={jobTitlesLoading}
                onMouseDown={e => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  setJobTitleSearch("");
                  getJobTitles(companyId, 1, "");
                }}
              >
                <FaTimes size={12} />
              </button>
            )}
          </div>
        )}
        panelFooterTemplate={
          <div className="d-flex align-items-center justify-content-between px-2 py-2" style={{ borderTop: "1px solid #e2e8f0" }} onMouseDown={e => e.stopPropagation()}>
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={jobTitlesLoading || jobTitlePage <= 1} onClick={() => getJobTitles(companyId, jobTitlePage - 1, jobTitleSearch)}>
              ‹ Prev
            </button>
            <span style={{ fontSize: 11 }}>
              {jobTitlesLoading ? "Loading..." : `Page ${jobTitlePage} / ${totalJobTitlePages}`}
            </span>
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={jobTitlesLoading || jobTitlePage >= totalJobTitlePages} onClick={() => getJobTitles(companyId, jobTitlePage + 1, jobTitleSearch)}>
              Next ›
            </button>
          </div>
        }
      />
    </div>
  );

  const tableHeader = (
    <div>
      <div>
        <h5 className="mb-1 fw-semibold">Placed candidate list</h5>
        <span className="text-muted small">
          Review selected candidates, latest stage logs, and resume previews.
        </span>
      </div>
    </div>
  );

  return (
    <Container fluid className="page-content allact-tabs agent-page candidate-outcome-page">
      <div className="page-title-box mb-0 recruiter-dashboard">
        <div className="agent-list-hero candidate-outcome-hero candidate-outcome-hero-selected">
          <div className="agent-list-title">
            <div className="agent-list-hero-icon">
              <FaUserCheck size={21} />
            </div>
            <div>
              <h1>Selected Candidates</h1>
              <p>Clean overview of placed candidates with logs and resume preview.</p>
            </div>
          </div>
          <div className="candidate-outcome-hero-actions">
            <FormGroup className="candidate-outcome-company mb-0">
              <Label for="CompanySelect" className="d-block">
                Select Company <span className="text-danger">*</span>
              </Label>
              <CompanySelector
                onCompanyChange={handleCompanyChange}
                showAllOption
                allOptionValue="all"
                allOptionLabel="All"
              />
            </FormGroup>
            <FormGroup className="candidate-outcome-job mb-0">
              <Label className="d-block">Job Order</Label>
              {jobOrderFilter}
            </FormGroup>
            <div className="agent-list-count-card mt-2">
              <span>Total Selected</span>
              <strong>{totalRecords || 0}</strong>
            </div>
          </div>
        </div>

        {loading && !candidateListData.length ? (
          <OrgTableShimmer columns={5} rows={7} titleWidth="210px" />
        ) : (
          <Card className="mb-3 shadow-md border-0 p-2">
            <DataTable
              value={candidateListData}
              header={tableHeader}
              dataKey="candidate_id"
              lazy
              paginator
              rows={lazyParams.rows}
              first={lazyParams.first}
              totalRecords={totalRecords}
              onPage={setLazyParams}
              sortField={sortState.field}
              sortOrder={sortState.order}
              onSort={event => {
                setSortState({ field: event.sortField, order: event.sortOrder });
                setCandidateListData(rows => sortCandidateRows(rows, event.sortField, event.sortOrder));
              }}
              loading={loading}
              responsiveLayout="scroll"
              rowsPerPageOptions={[5, 10, 20, totalRecords]}
              emptyMessage={
                <div className="candidate-empty-state">
                  <i className="pi pi-users" aria-hidden="true" />
                  <strong>No candidates available</strong>
                  <span>Try changing the company or job filters.</span>
                </div>
              }
              selection={selectedCandidates}
              onSelectionChange={e => setSelectedCandidates(e.value)}
              id="AllEmployees_TableView"
            >
              <Column header="Name" body={candidateNameTemplate} sortable sortField="first_name" />
              <Column field="email" header="Email" sortable />
              <Column field="mobile_phone" header="Phone" sortable />
              <Column header="Logs" body={stageLogsTemplate} />
              <Column
                field="total_experience"
                header="Experience"
                sortable
                body={viewCandidateExperience}
              />
              <Column header="Candidate Details" body={viewCandidateDetails} />
              <Column header="Is Selected" body={viewIsSelectedStatus} />
            </DataTable>
          </Card>
        )}
      </div>

      <ViewCandidatePopupModal
        isOpen={viewCandidatePopupShow}
        toggle={() => handleViewCandidate()}
        candidate={selectedCandidateDetails}
        onPrevious={handlePreviousCandidate}
        onNext={handleNextCandidate}
        previousDisabled={selectedCandidateIndex === null || selectedCandidateIndex <= 0}
        nextDisabled={selectedCandidateIndex === null || selectedCandidateIndex >= candidateListData.length - 1}
      />
      <CandidateLogsTimelineModal
        isOpen={logsTimelineShow}
        toggle={() => setLogsTimelineShow(false)}
        candidate={selectedLogsCandidate}
      />
    </Container>
  );
};

export default SelectedCandidates;
